import api from './api.service';
import type { ApiResponse } from '@/types';
import type { CustomerSatisfactionSurvey, CssParameterScores } from '@/constants/customer-satisfaction-survey';

function unwrap<T>(res: unknown): T | undefined {
  if (!res || typeof res !== 'object') return undefined;
  const r = res as ApiResponse<T>;
  if (r.data !== undefined) return r.data as T;
  return res as T;
}

export type CssListFilters = {
  customer_id?: string;
  status?: string;
  search?: string;
  date_from?: string;
  date_to?: string;
  satisfaction_result?: string;
  skip?: number;
  take?: number;
};

class CustomerSatisfactionSurveyService {
  async list(filters: CssListFilters = {}) {
    const q = new URLSearchParams();
    Object.entries(filters).forEach(([k, v]) => {
      if (v != null && v !== '') q.set(k, String(v));
    });
    const res = await api.get(`/sales/customer-satisfaction?${q}`);
    return (
      unwrap<{ rows: CustomerSatisfactionSurvey[]; total: number }>(res) ?? { rows: [], total: 0 }
    );
  }

  async getById(id: string) {
    const res = await api.get(`/sales/customer-satisfaction/${id}`);
    return unwrap<CustomerSatisfactionSurvey>(res);
  }

  async create(body: Record<string, unknown>) {
    const res = await api.post('/sales/customer-satisfaction', body);
    return unwrap<CustomerSatisfactionSurvey>(res);
  }

  async update(id: string, body: Record<string, unknown>) {
    const res = await api.put(`/sales/customer-satisfaction/${id}`, body);
    return unwrap<CustomerSatisfactionSurvey>(res);
  }

  async send(id: string, body?: { expires_days?: number; regenerate?: boolean }) {
    const res = await api.post(`/sales/customer-satisfaction/${id}/send`, body ?? {});
    return unwrap<CustomerSatisfactionSurvey>(res);
  }

  async review(id: string, body?: { sales_notes?: string; kpi_target_pct?: number }) {
    const res = await api.post(`/sales/customer-satisfaction/${id}/review`, body ?? {});
    return unwrap<CustomerSatisfactionSurvey>(res);
  }

  async cancel(id: string) {
    const res = await api.post(`/sales/customer-satisfaction/${id}/cancel`);
    return unwrap<CustomerSatisfactionSurvey>(res);
  }

  async getKpi() {
    const res = await api.get('/sales/customer-satisfaction/kpi');
    return unwrap<{ kpi_target_pct: number }>(res);
  }

  async getPublic(token: string) {
    const res = await api.get(`/sales/public/customer-satisfaction/${token}`);
    return unwrap<Record<string, unknown>>(res);
  }

  async submitPublic(
    token: string,
    body: {
      customer_name_snapshot?: string;
      customer_address?: string;
      scope_of_work?: string;
      project_or_po_reference?: string;
      feedback_date?: string;
      parameter_scores: CssParameterScores;
      overall_comments?: string;
      client_rep_name: string;
      client_rep_email: string;
      client_rep_signature?: string;
      client_rep_date?: string;
    }
  ) {
    const res = await api.post(`/sales/public/customer-satisfaction/${token}/submit`, body);
    return unwrap<Record<string, unknown>>(res);
  }
}

const customerSatisfactionSurveyService = new CustomerSatisfactionSurveyService();
export default customerSatisfactionSurveyService;
